/**
 * TravelExpensesPage — Dienstreisen-/Reisekostenerfassung (RK-7).
 *
 * Liste aller Reisen des aktuellen Scopes + Anlage/Bearbeitung (draft) +
 * abgerechnete/stornierte Detailansicht. Diese Seite orchestriert NUR — die
 * eigentliche Logik (Berechnung, Validierung, Persistenz) lebt in
 * `utils/travel/*` und `api/travelApi.ts` (RK-1..RK-3); die UI-Bausteine
 * leben in `components/travel/*`.
 *
 * Access gate: `canUseTravelExpenses` (config/travelExpensesAccess.ts) —
 * fail-closed, wenn weder Beta-Allowlist noch Flag+**Ultra** greifen (GA-Umzug
 * §3; der frühere „Flag+Premium"-Stand ist überholt). Die Route selbst ist
 * bereits flag-/beta-konditional registriert (`routes/dashboardRoutePaths.ts`).
 *
 * Upsell statt Leerrendern (Ultra-Upsell-Konsistenz 2026-07-29): bei aktivem
 * `TRAVEL_EXPENSES_FLAG` **und im eigenen Scope** tritt ein {@link FeatureTeaser}
 * an die Stelle des bisherigen `return null` — sonst wäre das Feature, das Ultra
 * verkaufen soll, für die gesamte Zielgruppe unsichtbar. Bei Flag AUS bleibt es
 * beim `return null` (kein Kaufanreiz für eine reine Beta-Erprobung; identische
 * Begründung wie `BankReconciliationPage.tsx`). Im Mandanten-Scope (StB) bleibt
 * es EBENFALLS bei `return null` — ein Berater wird nie mit dem Tarif-Zustand
 * eines fremden Tenants konfrontiert (F-1, T6-Auflage 3; diese Route ist als
 * einzige nicht `<AdvisorHidden>`-gewrappt, s. Access-Gate unten).
 *
 * Advisor-Sichtbarkeit (§7.2): bewusst KEIN `<AdvisorHidden>`-Wrapper
 * (Präzedenz `/dashboard/ausgaben`) — ein aktiver Advisor im Mandanten-Scope
 * sieht die Reisen lesend; Schreib-Aktionen werden hier zusätzlich defensiv
 * ausgeblendet (`useIsReadOnly`), die harte Sperre liegt bei den
 * Firestore-Rules (§7).
 *
 * @module TravelExpensesPage
 */

import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IconArrowLeft, IconDelete, IconError, IconNew, IconTravel } from '../icons';

import { useAuth } from '../context/AuthContext';
import { useIsReadOnly } from '../hooks/useIsReadOnly';
import {
  canUseForeignTravel,
  canUseRouteLookup,
  canUseTravelExpenses,
  getForeignRatesForUser,
  TRAVEL_EXPENSES_FLAG,
} from '../config/travelExpensesAccess';
import FeatureTeaser from '../components/premium/FeatureTeaser';
import { resolveTier } from '../config/entitlements';
import { CreateTravelTripInput, TravelTrip, UpdateTravelTripPatch } from '../types/travelTypes';
import type { TravelDayRecord, TravelMileage } from '../utils/travel/travelCalcTypes';
import type { TravelCalcInput } from '../utils/travel/travelAllowance';
import {
  cancelTravelTrip,
  createTravelTrip,
  deleteTravelTrip,
  getTravelTripsForUser,
  settleTravelTrip,
  updateTravelTrip,
} from '../api/travelApi';
import { lookupRouteDistance } from '../api/travelRouteApi';
import { centsToEuros } from '../utils/moneyUtils';
import logger from '../utils/logger';
import { formatTravelError } from '../utils/travel/travelErrorMessages';

import TripForm, { TripFormValue } from '../components/travel/TripForm';
import TripDayMealsTable from '../components/travel/TripDayMealsTable';
import TripMileageForm from '../components/travel/TripMileageForm';
import TripExpenseLinkPanel from '../components/travel/TripExpenseLinkPanel';
import TripSettlementSummary from '../components/travel/TripSettlementSummary';
import TripStatusBadge from '../components/travel/TripStatusBadge';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatCents(cents: number): string {
  return new Intl.NumberFormat('de-DE', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
  }).format(centsToEuros(cents));
}

function formatDateRange(departureISO: string, returnISO: string): string {
  const format = (iso: string): string => new Date(iso).toLocaleDateString('de-DE');
  return departureISO === returnISO ? format(departureISO) : `${format(departureISO)} – ${format(returnISO)}`;
}

/** Der lokale Bearbeitungszustand einer Reise — Anlage (neu) und Draft-Bearbeitung teilen sich diese Form. */
interface TripEditState {
  purpose: string;
  destinationCity: string;
  venueLabel: string | null;
  isFirstWorkplace: boolean;
  departureDateISO: string;
  departureTime: string;
  returnDateISO: string;
  returnTime: string;
  days: TravelDayRecord[];
  mileage: TravelMileage | null;
  linkedExpenseIds: string[];
}

function blankEditState(): TripEditState {
  return {
    purpose: '',
    destinationCity: '',
    venueLabel: null,
    isFirstWorkplace: false,
    departureDateISO: '',
    departureTime: '09:00',
    returnDateISO: '',
    returnTime: '17:00',
    days: [],
    mileage: null,
    linkedExpenseIds: [],
  };
}

function editStateFromTrip(trip: TravelTrip): TripEditState {
  return {
    purpose: trip.purpose,
    destinationCity: trip.destinationCity,
    venueLabel: trip.venueLabel,
    isFirstWorkplace: trip.isFirstWorkplace,
    departureDateISO: trip.departureDateISO,
    departureTime: trip.departureTime,
    returnDateISO: trip.returnDateISO,
    returnTime: trip.returnTime,
    days: trip.days,
    mileage: trip.mileage,
    linkedExpenseIds: trip.linkedExpenseIds,
  };
}

/**
 * RK-21 (§14.4 Save-Pfad, V10): entfernt leere optionale Route-Lookup-Felder VOR dem
 * API-Aufruf, statt sie als leeren String (`''`) mitzuschreiben. `TravelMileage.originAddress`/
 * `destinationAddress` sind bewusst OPTIONAL (§14.3) — Firestore lehnt `undefined`-Feldwerte ab
 * (`assertNoUndefinedValues`, `travelApi.ts`), also muss der SCHLÜSSEL fehlen, nicht nur der
 * Wert `undefined` sein. Während der Bearbeitung (React-State) ist `''` unproblematisch (kein
 * Firestore-Write) — erst hier, am Übergang zur API, wird bereinigt.
 */
function stripEmptyMileageFields(mileage: TravelMileage | null): TravelMileage | null {
  if (mileage === null) return null;
  const { originAddress, destinationAddress, suggestedKm, kmSource, ...rest } = mileage;
  const cleaned: TravelMileage = { ...rest };
  const trimmedOrigin = originAddress?.trim();
  if (trimmedOrigin) cleaned.originAddress = trimmedOrigin;
  const trimmedDestination = destinationAddress?.trim();
  if (trimmedDestination) cleaned.destinationAddress = trimmedDestination;
  if (suggestedKm !== undefined) cleaned.suggestedKm = suggestedKm;
  if (kmSource !== undefined) cleaned.kmSource = kmSource;
  return cleaned;
}

function buildCalcInput(state: TripEditState): TravelCalcInput {
  return {
    departureDateISO: state.departureDateISO,
    departureTime: state.departureTime,
    returnDateISO: state.returnDateISO,
    returnTime: state.returnTime,
    days: state.days,
    mileage: state.mileage,
    isFirstWorkplace: state.isFirstWorkplace,
  };
}

function buildTripFormValue(state: TripEditState): TripFormValue {
  return {
    purpose: state.purpose,
    destinationCity: state.destinationCity,
    venueLabel: state.venueLabel,
    isFirstWorkplace: state.isFirstWorkplace,
    departureDateISO: state.departureDateISO,
    departureTime: state.departureTime,
    returnDateISO: state.returnDateISO,
    returnTime: state.returnTime,
  };
}

// ─── Upsell-Teaser ────────────────────────────────────────────────────────────

/**
 * Upsell-Teaser für nicht-berechtigte Nutzer bei aktivem
 * `TRAVEL_EXPENSES_FLAG` (Ziel-Tier Ultra). Ersetzt das bisherige
 * `return null` — Muster `BankReconciliationPage`/`ReceiptScanPage`
 * (`layout="page"`, der Teaser IST die gesamte Seite).
 *
 * Rein präsentational: keine Firestore-Reads, keine Hooks außer denen von
 * `FeatureTeaser` selbst — mountbar für jeden Nutzer unabhängig vom
 * Auth-/Scope-Zustand.
 *
 * Wording-Vorsicht: der Teaser bewirbt ausschließlich den TATSÄCHLICH
 * ausgelieferten Umfang. Kein Steuerberatungs-Versprechen — die App erfasst und
 * rechnet nach den amtlichen Pauschalen, sie berät nicht.
 *
 * Jurist-MINOR M-3 (Re-Loop 1, §6.1) — weiterhin gültig:
 *   „Kilometerpauschale" → „pauschale Kilometersätze". Ersteres ist die
 *   ENTFERNUNGSpauschale für Wohnung↔erste Tätigkeitsstätte
 *   (§ 9 Abs. 1 S. 3 Nr. 4 EStG), für Dienstreisen gilt
 *   § 9 Abs. 1 S. 3 Nr. 4a S. 2 EStG. Nicht paraphrasieren.
 *
 * Jurist-MINOR R3-1 (Re-Loop 4, A-4-Wiedervorlage nach PR #316):
 * Die Inlands-Einschränkung aus M-3 ist ENTFALLEN. Sie stand dort, weil
 * `TRAVEL_FOREIGN_FLAG` aus war und `getForeignRatesForUser` regulären
 * Ultra-Kunden `undefined` lieferte — eine Auslandsreise wäre mit Inlandssätzen
 * gerechnet worden. PR #316 hat `REACT_APP_TRAVEL_FOREIGN_ENABLED=true`
 * geflippt und den vollständigen BMF-2026-Auslandsdatensatz eingebracht
 * (O-11/O-13 abgenommen), womit die Prämisse der Auflage weggefallen ist. „im
 * Inland" wäre jetzt eine positiv FALSCHE Angabe über den Leistungsumfang.
 *
 * Drei Grenzen, die der freigegebene Wortlaut einhält — beim nächsten Umbau
 * NICHT aufweichen:
 *   1. KEINE Länderzahl („~166 Länder"). Sie hätte dasselbe Verfallsdatum wie
 *      ein Betrag; die Luxemburg-Auffangregel macht sie entbehrlich.
 *   2. `teaserHighlight2` bleibt UNVERÄNDERT. `TRAVEL_ROUTE_ENABLED` ist weiter
 *      aus (`.env.production:196-198`) — eine automatische Distanzermittlung
 *      darf hier nicht beworben werden. Der Nutzer gibt die Distanz selbst ein.
 *   3. In den 5 Fremdsprachen ist der Bezug auf die DEUTSCHEN Pauschalen
 *      zwingend („official German flat rates", „forfaits officiels allemands",
 *      …). Sonst läse ein französischer Nutzer französische Sätze hinein,
 *      während die App nach § 9 Abs. 4a S. 5 EStG rechnet (§ 5 Abs. 2 Nr. 1 UWG,
 *      dieselbe Fehlerklasse wie M-7 bei der UStVA).
 * Konkrete Beträge gehören laut Jurist NICHT in Werbetexte (jährliche
 * BMF-Anpassung ⇒ Veraltungsrisiko) — hier steht bewusst keine Zahl. Der
 * Quellen-/Stand-Hinweis gehört auf die PRODUKTfläche (R3-2/R3-3,
 * `TripSettlementSummary`), nicht hierher.
 */
const TravelExpensesTeaser: React.FC = () => (
  <FeatureTeaser
    layout="page"
    icon={<IconTravel size={30} aria-hidden />}
    badgeKey="travelExpenses.teaserBadge"
    badgeDefault="Ultra-Funktion"
    titleKey="travelExpenses.teaserTitle"
    titleDefault="Reisekosten & Dienstreisen"
    descriptionKey="travelExpenses.teaserDescription"
    descriptionDefault="Erfassen Sie Verpflegungsmehraufwand, Fahrtkosten und Übernachtungen pro Reise — FaeBill rechnet Reisen im In- und Ausland nach den amtlichen Pauschalen aus."
    highlights={[
      {
        labelKey: 'travelExpenses.teaserHighlight1',
        defaultLabel: 'Verpflegungspauschalen je Reisetag automatisch berechnet',
      },
      {
        labelKey: 'travelExpenses.teaserHighlight2',
        defaultLabel: 'Pauschale Kilometersätze für jede Fahrt berechnet',
      },
      {
        labelKey: 'travelExpenses.teaserHighlight3',
        defaultLabel: 'Belege direkt der Reise zuordnen',
      },
    ]}
    ctaKey="travelExpenses.teaserCta"
    ctaDefault="Jetzt auf Ultra upgraden"
    promotionId="feature_teaser_travel_expenses"
    creativeSlot="page"
  />
);

// ─── TravelExpensesPage ───────────────────────────────────────────────────────

const TravelExpensesPage: React.FC = () => {
  const { t } = useTranslation('travel');
  const { currentUser, effectiveScopeUid, sellerData } = useAuth();
  // Reisekosten ist ab GA ein Ultra-Feature (modules-ga-freischaltung.md §3):
  // der effektive Tier ersetzt das frühere `isPremium` in allen Gate-Aufrufen.
  const tier = resolveTier(sellerData);
  // F-1 (Re-Loop 2): „betrachte ich meinen EIGENEN Tenant?" — Muster
  // `DatevExportPage.tsx:116`. Steuert ausschließlich, ob der Upsell-Teaser
  // gezeigt werden darf (s. Access-Gate unten); auf den Inhalt der Seite für
  // Berechtigte hat es keinen Einfluss.
  const isOwnScope = !!currentUser && effectiveScopeUid === currentUser.uid;
  const isReadOnly = useIsReadOnly();

  const [trips, setTrips] = useState<TravelTrip[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [view, setView] = useState<'list' | 'edit'>('list');
  const [selectedTripId, setSelectedTripId] = useState<string | null>(null);
  const [editState, setEditState] = useState<TripEditState>(blankEditState());

  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [settling, setSettling] = useState(false);
  const [settleError, setSettleError] = useState<string | null>(null);
  const [cancelling, setCancelling] = useState(false);
  const [cancelError, setCancelError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);

  const reloadTrips = async (): Promise<void> => {
    setError(null);
    try {
      const data = await getTravelTripsForUser();
      setTrips(data);
    } catch (err) {
      logger.error('TravelExpensesPage: load failed', err);
      setError(formatTravelError(err, t));
    }
  };

  useEffect(() => {
    // T-1 (Tester-Fund, Re-Loop 1 2026-07-29): das Access-Gate MUSS hier
    // mitgeprüft werden, nicht erst beim Rendern. Der Effekt lief vorher für
    // JEDEN eingeloggten Nutzer und feuerte `getTravelTripsForUser()` auch
    // dann, wenn am Ende der Upsell-Teaser (oder `null`) gerendert wird — ein
    // überflüssiger owner-scoped Read für jeden Free-/Premium-Nutzer, der
    // `/dashboard/reisekosten` öffnet, und ein Widerspruch zur Zusage „rein
    // präsentational: keine Firestore-Reads" im `TravelExpensesTeaser`-JSDoc.
    // Muster 1:1 aus `UmsatzsteuerPage.tsx` (dort war die Guard-Klausel schon
    // korrekt). Rules of Hooks: der Effekt bleibt UNBEDINGT deklariert, nur
    // sein Rumpf steigt früh aus.
    if (!currentUser || !canUseTravelExpenses(currentUser.uid, tier)) {
      setLoading(false);
      return;
    }
    let cancelledEffect = false;
    setLoading(true);
    getTravelTripsForUser()
      .then((data) => {
        if (!cancelledEffect) setTrips(data);
      })
      .catch((err) => {
        if (!cancelledEffect) {
          logger.error('TravelExpensesPage: initial load failed', err);
          setError(formatTravelError(err, t));
        }
      })
      .finally(() => {
        if (!cancelledEffect) setLoading(false);
      });
    return () => {
      cancelledEffect = true;
    };
    // `tier` gehört in die Dep-Liste: wird ein Nutzer während der Sitzung zu
    // Ultra (Checkout-Rückkehr, `sellerData`-Update), muss der Load nachziehen.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser, effectiveScopeUid, tier]);

  const selectedTrip = useMemo(
    () => trips.find((trip) => trip.id === selectedTripId) ?? null,
    [trips, selectedTripId],
  );

  // Fail-closed access gate — the route may be mounted (env-level flag/beta
  // check) while THIS user is neither on the beta allowlist nor ultra+flag.
  if (!canUseTravelExpenses(currentUser?.uid, tier)) {
    // Ultra-Upsell-Konsistenz 2026-07-29: Flag AN + kein Ultra ⇒ Teaser
    // (Upsell statt Leerrendern). Flag AUS ⇒ byte-identisch zum bisherigen
    // `return null` — dann hat nur die Beta-Allowlist Zugriff und ein
    // Kaufanreiz für eine reine Beta-Erprobung wäre irreführend
    // (`BankReconciliationPage.tsx`-Begründung).
    //
    // F-1 (Reviewer, Re-Loop 2): zusätzlich `isOwnScope`. `/dashboard/reisekosten`
    // ist als EINZIGE der betroffenen Routen bewusst NICHT `<AdvisorHidden>`-
    // gewrappt (`routes/dashboardChildren.tsx:299-304`, RK-7 §7.2) — ein StB im
    // Mandanten-Scope, der die Route direkt aufruft (Deeplink/Bookmark; die Nav
    // blendet sie via `resolveTravelExpensesVisibility` korrekt aus), bekäme sonst
    // „Jetzt auf Ultra upgraden" zu sehen. `tier` ist der Tarif des EINGELOGGTEN
    // Nutzers (nur `effectiveScopeUid` wechselt beim Mandanten-Wechsel, nicht
    // `sellerData`) — der StB würde also mit dem Tarif-Zustand eines fremden
    // Tenants konfrontiert. Das verletzt T6-Auflage 3, die dieser PR im
    // Nav-Resolver selbst umsetzt (`navigation.ts` `resolveTravelExpensesVisibility`).
    // Muster 1:1 aus `DatevExportPage.tsx:333` (`if (DATEV_EXPORT_FLAG && isOwnScope)`).
    //
    // Bewusst `isOwnScope` statt des bereits vorhandenen `isReadOnly`: `useIsReadOnly`
    // ist rollenbasiert (`activeRole === 'advisor'`) und laut eigenem Modul-JSDoc
    // fail-OPEN („nie für die Autorisierung"). Der Scope-Vergleich ist fail-safe —
    // bei jedem fremden Scope, aus welchem Grund auch immer, unterbleibt der CTA.
    if (TRAVEL_EXPENSES_FLAG && isOwnScope) {
      return <TravelExpensesTeaser />;
    }
    return null;
  }

  const openCreate = (): void => {
    setSelectedTripId(null);
    setEditState(blankEditState());
    setSaveError(null);
    setSettleError(null);
    setCancelError(null);
    setView('edit');
  };

  const openTrip = (trip: TravelTrip): void => {
    setSelectedTripId(trip.id);
    setEditState(editStateFromTrip(trip));
    setSaveError(null);
    setSettleError(null);
    setCancelError(null);
    setView('edit');
  };

  const backToList = (): void => {
    setView('list');
    setSelectedTripId(null);
  };

  const buildPatchFromEditState = (): Omit<CreateTravelTripInput, 'travelerType'> => ({
    purpose: editState.purpose,
    destinationCity: editState.destinationCity,
    venueLabel: editState.venueLabel,
    isFirstWorkplace: editState.isFirstWorkplace,
    departureDateISO: editState.departureDateISO,
    departureTime: editState.departureTime,
    returnDateISO: editState.returnDateISO,
    returnTime: editState.returnTime,
    days: editState.days,
    // RK-21 (V10): leere optionale Adress-/Route-Lookup-Felder werden hier gestrippt (nicht
    // schon im Formular-State), siehe {@link stripEmptyMileageFields}.
    mileage: stripEmptyMileageFields(editState.mileage),
    linkedExpenseIds: editState.linkedExpenseIds,
  });

  const handleCreate = async (): Promise<void> => {
    setSaving(true);
    setSaveError(null);
    try {
      const input: CreateTravelTripInput = { ...buildPatchFromEditState(), travelerType: 'unternehmer' };
      const id = await createTravelTrip(input);
      await reloadTrips();
      setSelectedTripId(id);
    } catch (err) {
      logger.error('TravelExpensesPage: create failed', err);
      setSaveError(formatTravelError(err, t));
    } finally {
      setSaving(false);
    }
  };

  const handleSaveDraft = async (): Promise<void> => {
    if (!selectedTripId) return;
    setSaving(true);
    setSaveError(null);
    try {
      const patch: UpdateTravelTripPatch = buildPatchFromEditState();
      await updateTravelTrip(selectedTripId, patch);
      await reloadTrips();
    } catch (err) {
      logger.error('TravelExpensesPage: save failed', err);
      setSaveError(formatTravelError(err, t));
    } finally {
      setSaving(false);
    }
  };

  const handleSettle = async (): Promise<void> => {
    if (!selectedTripId) return;
    setSettling(true);
    setSettleError(null);
    try {
      // "Abrechnen" speichert die aktuellen Formular-Änderungen zuerst (die
      // Live-Vorschau rechnet über dem UNGESPEICHERTEN Zustand — settleTravelTrip
      // rechnet serverseitig über dem GESPEICHERTEN Dokument neu, damit beide
      // Werte identisch sind, muss der Stand vorher persistiert sein).
      const patch: UpdateTravelTripPatch = buildPatchFromEditState();
      await updateTravelTrip(selectedTripId, patch);
      // V19 Gate-Parity (§13.7): `tier` durchreichen, damit `settleTravelTrip` dieselbe
      // Auslands-Gate-Entscheidung trifft wie die Live-Vorschau unten (`getForeignRatesForUser`).
      await settleTravelTrip(selectedTripId, { tier });
      await reloadTrips();
    } catch (err) {
      logger.error('TravelExpensesPage: settle failed', err);
      setSettleError(formatTravelError(err, t));
    } finally {
      setSettling(false);
    }
  };

  const handleCancel = async (reason: string): Promise<void> => {
    if (!selectedTripId) return;
    setCancelling(true);
    setCancelError(null);
    try {
      await cancelTravelTrip(selectedTripId, reason);
      await reloadTrips();
    } catch (err) {
      logger.error('TravelExpensesPage: cancel failed', err);
      setCancelError(formatTravelError(err, t));
    } finally {
      setCancelling(false);
    }
  };

  const handleDelete = async (): Promise<void> => {
    if (!selectedTripId) return;
    setDeleting(true);
    try {
      await deleteTravelTrip(selectedTripId);
      await reloadTrips();
      backToList();
    } catch (err) {
      logger.error('TravelExpensesPage: delete failed', err);
      setSaveError(formatTravelError(err, t));
    } finally {
      setDeleting(false);
    }
  };

  const isEditableDraft = selectedTrip === null || selectedTrip.status === 'draft';

  // ── Render: edit/detail view ──────────────────────────────────────────────
  if (view === 'edit') {
    return (
      <div className="min-h-screen bg-surface p-4 md:p-6">
        <div className="max-w-4xl mx-auto space-y-4">
          <div className="flex items-center justify-between">
            <button
              type="button"
              onClick={backToList}
              className="flex items-center gap-2 text-sm text-muted hover:text-primary transition-colors"
            >
              <IconArrowLeft size={14} />
              {t('detail.back')}
            </button>
            {selectedTrip && <TripStatusBadge status={selectedTrip.status} />}
          </div>

          <TripForm
            value={buildTripFormValue(editState)}
            onChange={(value) => setEditState((prev) => ({ ...prev, ...value }))}
            disabled={!isEditableDraft || isReadOnly}
          />

          <TripDayMealsTable
            departureDateISO={editState.departureDateISO}
            returnDateISO={editState.returnDateISO}
            days={editState.days}
            onChange={(days) => setEditState((prev) => ({ ...prev, days }))}
            disabled={!isEditableDraft || isReadOnly}
            // RK-15 (§13.10 Consumer-Blast-Radius): fail-closed über den Sub-Flag-Gate-Helfer,
            // NIE eine eigene Gate-Prüfung hier.
            foreignEnabled={canUseForeignTravel(currentUser?.uid, tier)}
            foreignRates={getForeignRatesForUser(currentUser?.uid, tier)}
          />

          <TripMileageForm
            value={editState.mileage}
            onChange={(mileage) => setEditState((prev) => ({ ...prev, mileage }))}
            disabled={!isEditableDraft || isReadOnly}
            // RK-21 (§14.3 E20 Consumer-Blast-Radius): fail-closed über den Sub-Flag-Gate-Helfer,
            // NIE eine eigene Gate-Prüfung hier (Muster RK-15 `canUseForeignTravel` oben).
            routeLookupEnabled={canUseRouteLookup(currentUser?.uid, tier)}
            onRouteLookup={lookupRouteDistance}
          />

          <TripExpenseLinkPanel
            scopeUid={effectiveScopeUid}
            linkedExpenseIds={editState.linkedExpenseIds}
            onChange={(linkedExpenseIds) => setEditState((prev) => ({ ...prev, linkedExpenseIds }))}
            disabled={!isEditableDraft || isReadOnly}
          />

          <TripSettlementSummary
            status={selectedTrip?.status ?? 'draft'}
            calcInput={isEditableDraft ? buildCalcInput(editState) : null}
            settlement={selectedTrip?.settlement ?? null}
            cancellationReason={selectedTrip?.cancellationReason ?? null}
            cancelledAtISO={selectedTrip?.cancelledAtISO ?? null}
            onSettle={!isReadOnly && selectedTripId ? handleSettle : undefined}
            // Bug-3-Fix (bug-analyse-2026-07-17.md): vor dem ersten Speichern gibt es noch
            // keine `selectedTripId`, also kein `onSettle` — signalisiert dem Bereich, den
            // Button TROTZDEM (disabled + Hinweis) zu zeigen statt ihn ganz zu verstecken.
            // Read-Only bleibt unverändert komplett ausgeblendet (kein Speichern möglich).
            settleBlockedReason={!isReadOnly && !selectedTripId ? 'notSaved' : null}
            settling={settling}
            settleError={settleError}
            onCancel={!isReadOnly && selectedTrip?.status === 'settled' ? handleCancel : undefined}
            cancelling={cancelling}
            cancelError={cancelError}
            // RK-15 (§13.10 Consumer-Blast-Radius, V19 Gate-Parity): derselbe Helfer wie
            // `handleSettle` oben (`settleTravelTrip(..., { tier })`) — Vorschau UND
            // Abrechnen treffen so IMMER dieselbe Gate-Entscheidung.
            foreignRates={getForeignRatesForUser(currentUser?.uid, tier)}
          />

          {!isReadOnly && isEditableDraft && (
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="flex gap-2">
                {saveError && <span className="text-xs text-[var(--tone-danger-fg)]">{saveError}</span>}
              </div>
              <div className="flex gap-2">
                {selectedTripId && (
                  <button
                    type="button"
                    onClick={handleDelete}
                    disabled={deleting}
                    className="flex items-center gap-2 px-3 py-2 text-sm text-[var(--tone-danger-fg)] hover:bg-[var(--tone-danger-bg)] rounded-lg transition-colors"
                  >
                    <IconDelete size={14} />
                    {t('detail.deleteButton')}
                  </button>
                )}
                <button
                  type="button"
                  onClick={selectedTripId ? handleSaveDraft : handleCreate}
                  disabled={saving}
                  className="px-4 py-2 text-sm font-medium bg-surface-inset hover:bg-surface-hover disabled:opacity-50 text-primary rounded-lg transition-colors"
                >
                  {saving ? t('detail.saving') : selectedTripId ? t('detail.saveButton') : t('detail.createButton')}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // ── Render: list view ───────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-surface p-4 md:p-6">
      <div className="max-w-6xl mx-auto">
        <div className="mb-6 flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-bold text-primary flex items-center gap-3">
              <IconTravel size={24} className="text-accent-ink" />
              {t('pageTitle')}
            </h1>
            <p className="text-muted text-sm mt-1">{t('pageSubtitle')}</p>
          </div>
          {!isReadOnly && (
            <button
              type="button"
              onClick={openCreate}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium bg-indigo-600 hover:bg-indigo-500 text-[#ffffff] rounded-lg transition-colors"
            >
              <IconNew />
              {t('list.newTrip')}
            </button>
          )}
        </div>

        {loading && <div className="flex justify-center py-16 text-muted text-sm">{t('list.loading')}</div>}

        {!loading && error && (
          <div className="bg-[var(--tone-danger-bg)] border border-[var(--tone-danger-border)] rounded-xl p-6 text-center">
            <IconError size={32} className="mx-auto mb-3 text-[var(--tone-danger-fg)]" />
            <p className="text-[var(--tone-danger-fg)] font-medium">{t('list.error.title')}</p>
            <p className="text-[var(--tone-danger-fg)] text-xs mt-1 font-mono">{error}</p>
            <button
              onClick={() => {
                setLoading(true);
                reloadTrips().finally(() => setLoading(false));
              }}
              className="mt-4 px-4 py-2 text-sm bg-red-700 hover:bg-red-600 text-[#ffffff] rounded-lg transition-colors"
            >
              {t('list.error.retry')}
            </button>
          </div>
        )}

        {!loading && !error && trips.length === 0 && (
          <div className="text-center py-16">
            <IconTravel size={48} strokeWidth={1.25} className="mx-auto mb-4 text-faint" />
            <p className="text-muted font-medium">{t('list.empty')}</p>
            <p className="text-faint text-sm mt-1">{t('list.emptyHint')}</p>
          </div>
        )}

        {!loading && !error && trips.length > 0 && (
          <div className="overflow-x-auto rounded-xl border border-hairline">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-surface-raised text-muted text-xs uppercase tracking-wide">
                  <th className="px-4 py-3 text-left font-medium">{t('list.columns.purpose')}</th>
                  <th className="px-4 py-3 text-left font-medium">{t('list.columns.destination')}</th>
                  <th className="px-4 py-3 text-left font-medium">{t('list.columns.dates')}</th>
                  <th className="px-4 py-3 text-right font-medium">{t('list.columns.total')}</th>
                  <th className="px-4 py-3 text-center font-medium">{t('list.columns.status')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[color:var(--border-hairline)]">
                {trips.map((trip) => (
                  <tr
                    key={trip.id}
                    onClick={() => openTrip(trip)}
                    className="bg-[rgb(var(--surface-raised-rgb)/0.5)] hover:bg-surface-raised transition-colors cursor-pointer"
                  >
                    <td className="px-4 py-3 text-primary font-medium truncate max-w-[220px]">
                      {trip.purpose || t('list.noPurpose')}
                    </td>
                    <td className="px-4 py-3 text-secondary truncate max-w-[160px]">{trip.destinationCity}</td>
                    <td className="px-4 py-3 text-muted text-xs whitespace-nowrap">
                      {formatDateRange(trip.departureDateISO, trip.returnDateISO)}
                    </td>
                    <td className="px-4 py-3 text-right font-mono text-xs whitespace-nowrap">
                      {trip.settlement ? (
                        <span className="text-primary font-medium">{formatCents(trip.settlement.calc.totalCents)}</span>
                      ) : (
                        <span className="text-faint">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <TripStatusBadge status={trip.status} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default TravelExpensesPage;
